import React, { ReactNode } from 'react';
import { useFormik, FormikHelpers } from 'formik';
import { VoucherEntry } from '../helpers/models/Models';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { convertFormDataToVoucherEntries, extractUserID } from '../helpers/extractData';
import { postDataToTheServer } from '../helpers/api';
import AppURL from '../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../constants/Toast';
import { ADToBSConvert, getMonth, getYear } from '../constants/DateFormat';
import { useDispatch } from 'react-redux';
import { CONTRA_VOUCHER_ENTRY_LIST, CONTRA_VOUCHER_INFO_LIST, RESET_BILLING_PARTICULAR_LIST } from '../store/accountSlice';
import { coucherEntryvalidationSchema } from '../helpers/validation';
import { BILLING_REPORT_LIST_VIEW } from '../store/inventorySlice';

export interface VoucherContextType {
    ResetForm: () => Promise<void>;
    customForm: () => Promise<void>;
    initialValues: VoucherEntry;
    values: VoucherEntry;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    handleChange: (e: React.ChangeEvent<any>) => void;
    handleSelectBox: (name: string, value: any) => void;
    errors: { [key: string]: string | undefined };
    touched: { [key: string]: boolean };
    handleBlur: (e: React.FocusEvent<any>) => void;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
}

export const VoucherContext = React.createContext<VoucherContextType | undefined>(undefined);

interface VoucherEntryContextProps {
    children: ReactNode;
}
const VoucherEntryContext: React.FC<VoucherEntryContextProps> = ({ children }) => {
    const { SET_FULL_SOFT_DATA } = useSelector((state: RootState) => state.software);
    const userId = extractUserID();
    const dispatch = useDispatch();
    const { current_fiscal_year_list } = useSelector((state: RootState) => state.software);
    const customForm = async () => {
        setFieldValue("debitamt", '');
        setFieldValue("creditamt", '');
        setFieldValue("chequeno", '');
        setFieldValue("bearername", '');
        setFieldValue("dep_particular_id", '');
        setFieldValue("bank_sglsn", '');
    };
    const bill_no = localStorage.getItem("bill_no");
    const saveBillNo = (bill_no: number) => {
        if (bill_no !== undefined && bill_no !== null) {
            localStorage.setItem("bill_no", bill_no.toString());
        } else {
            console.error("bill_no is undefined or null");
        }
    };
    const initialValues: VoucherEntry = {
        date_en: new Date().toISOString().split('T')[0] || "",
        fiscal_year_date: current_fiscal_year_list?.fiscal_year?.fiscal_year_name || "",
        fiscal_start: current_fiscal_year_list?.fiscal_year?.from_date_n || "",
        date_ne: "",
        debitamt: "",
        creditamt: "",
        user_no: parseInt(userId?.toString() || "1"),
        chequeno: "",
        bearername: "",
        billing_no: "",
        voucher_type: "1",
        is_debit: null,
        view_full_leader: false,
        part_is_debit: null,
        trancomment: "",
        bill_print: 1,
        glid: "",
        ledger_start_date: current_fiscal_year_list?.fiscal_year?.from_date_n || "",
        ledger_end_date: SET_FULL_SOFT_DATA?.today_np,
        cash_sglsn: "",
        voucher_no: "",
        voucher_no_1: "",
        trans_counter_code: "",
        voucher_kind: "",
        member_sn: "",
        depositmonth: "",
        deposityear: "",
        particularid: "",
        continue_bill: 0,
        sglsn: "",
        bank_sglsn: "",
        dep_particular_id: "",
        mem_cate_id: 0,
        particular_name: "",
        billingcomment: ""
    };
    const contiBillNo = localStorage.getItem("bill_no")
    const formik = useFormik<VoucherEntry>({
        initialValues,
        validationSchema: coucherEntryvalidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action: FormikHelpers<VoucherEntry>) => {
            const formData = new FormData();
            Object.keys(values).forEach((key) => {
                const value = (values as Record<string, any>)[key];
                if (value !== undefined && value !== null && value !== "") {
                    formData.append(key, value);
                }
            });
            formData.append("member_sn", values.mem_cate_id === 1 ? values.member_sn : "0");
            formData.append("date_ne", ADToBSConvert(values.date_en));
            formData.append("depositmonth", getMonth(values.date_en));
            formData.append("deposityear", getYear(values.date_en));
            formData.append("bill_print", values?.bill_print === 1 ? '1' : "0");
            formData.append(
                "continue_bill",
                contiBillNo !== null && values?.bill_print === 1
                    ? bill_no?.toString() ?? "0"
                    : "0"
            );

            try {
                if (values.voucher_type === "1") {
                    if (parseFloat(values.creditamt || "0") === 0 && parseFloat(values.debitamt || "0") === 0) {
                        ToastWarning({ message: "Please enter either a debit or credit amount" });
                        return;
                    }
                    if (!values?.dep_particular_id) {
                        ToastWarning({ message: "Please Select Cash Or Bank" });
                        return;
                    }
                    //   Check if debit/credit indicators match
                    if ((values?.is_debit) === values?.part_is_debit) {
                        ToastWarning({ message: "Please Check Debit Credit" });
                        return;
                    }
                    await postDataToTheServer(AppURL.AccountURL + "account/single/voucher/entry/create/view/", formData).then((res) => {
                        if (res?.status === 200) {
                            ToastSuccess({ message: res?.message });
                            saveBillNo(res?.receipt_no);
                            dispatch(BILLING_REPORT_LIST_VIEW(res));
                            customForm();
                        } else if (res?.status === 400) {
                            ToastWarning(res?.message);
                        } else {
                            // ToastError("Something Error ")
                        }
                    });
                } else {
                    if (parseFloat(values.creditamt || "0") === 0 && parseFloat(values.debitamt || "0") === 0) {
                        ToastWarning({ message: "Please enter either a debit or credit amount" });
                        return;
                    }
                    const voucherEntries = convertFormDataToVoucherEntries(formData);
                    dispatch(CONTRA_VOUCHER_ENTRY_LIST(voucherEntries));
                    customForm();
                    dispatch(CONTRA_VOUCHER_INFO_LIST({
                        fiscal_start: current_fiscal_year_list?.fiscal_year?.from_date_n || "",
                        user_id: parseInt(userId?.toString() || "1"),
                        date_enn: values.date_en || "",
                        date_ne: ADToBSConvert(values.date_en)
                    }));

                }
            } catch (error) {
                console.log(error);
            }
        },
    });

    const { values, handleChange, errors, touched, handleBlur, handleSubmit, setFieldValue } = formik;
    const ResetForm = async () => {
        formik.resetForm();
    };

    const handleSelectBox = (name: string, value: any) => {
        setFieldValue(name, value);
    };

    return (
        <VoucherContext.Provider
            value={{
                ResetForm,
                initialValues,
                values,
                handleChange,
                handleSelectBox,
                errors,
                touched,
                setFieldValue,
                handleBlur,
                handleSubmit,
                customForm
            }}
        >
            {children}
        </VoucherContext.Provider>
    );
};

export default VoucherEntryContext;
